import React from 'react';
import PropTypes from 'prop-types';

// Config
import { uiConfig } from '../../../../../../../../../../common/conf/codes';

// Components
import { DualList, DualListRowName } from '../../../../../../../../../../components/DualList';
import { Button } from '../../../../../../../../../../components/Button';
import { Dialog } from '../../../../../../../../../../components/Dialog';
import { DialogHeader } from '../../../../../../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../../../../../../components/DialogFooter';

// Enums
import { dtoState } from '../../../../../../../../../../common/enums/dtoState';

const reduceListItems = (listItems = []) =>
    listItems.reduce(
        (acc, listItem) => ({
            ...acc,
            [listItem?.openCdsConceptDTO?.codeId]: listItem.dtoState !== dtoState.Deleted
        }),
        {}
    );

export const ConceptSelectionModal = ({ closeDialog, onApply, allItems, partId, currentListItems = [] }) => {
    const [selected, setSelected] = React.useState({});
    const selectCode = React.useCallback(
        (id) => {
            if (!Object.hasOwn(selected, id)) {
                setSelected({
                    ...selected,
                    [id]: true
                });
            } else {
                setSelected({
                    ...selected,
                    [id]: !selected[id]
                });
            }
        },
        [selected, setSelected]
    );
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const selectedCount = React.useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
    const wrapConcept = React.useCallback(
        ({ code }) => ({
            '@class': 'org.cdsframework.dto.CriteriaPredicatePartConceptDTO',
            dtoState: dtoState.New,
            partId,
            openCdsConceptDTO: code,
            uuid: uuid4()
        }),
        [partId]
    );

    React.useEffect(() => {
        setSelected(reduceListItems(currentListItems));
    }, [currentListItems, setSelected]);

    const prepareSelection = React.useCallback(() => {
        const currentlySelected = [...currentListItems];
        const openCdsConceptDTOUpdated = Object.entries(selected).reduce((acc, [currKey, currVal]) => {
            const found = currentlySelected.find((item) => item?.openCdsConceptDTO?.codeId === currKey);

            if (found) {
                // If deselected, result without adding
                if (!currVal && found.dtoState === dtoState.New) {
                    return acc;
                }

                const newDtoState = !currVal
                    ? dtoState.Deleted
                    : found.dtoState === dtoState.Deleted
                    ? dtoState.Updated
                    : found.dtoState;

                return [
                    ...acc,
                    {
                        ...found,
                        dtoState: newDtoState
                    }
                ];
            } else if (currVal) {
                const code = allItems.find((code) => currKey === code[uiConfig.primaryKey]);
                return [
                    ...acc,
                    wrapConcept({
                        code
                    })
                ];
            } else {
                return acc;
            }
        }, []);

        onApply(openCdsConceptDTOUpdated);
        closeDialog();
    }, [selected, allItems, currentListItems, onApply]);

    const renderRowContents = (item, isSelected) => {
        return (
            <DualListRowName>
                {item?.displayName} ({item?.code})
            </DualListRowName>
        );
    };

    return (
        <Dialog close={CloseDialog} size="full" visible id="criteria-manager-concept-table-modal">
            <DialogHeader close={CloseDialog}>Concept Selection Table</DialogHeader>
            <DialogBody style={{ paddingBottom: 0 }}>
                <DualList
                    allItems={allItems}
                    selected={selected}
                    onSelect={(item) => selectCode(item.codeId)}
                    isLoading={false}
                    renderRowContents={renderRowContents}
                    primaryKey={'codeId'}
                    contentType={'concepts'}
                    searchableProperties={['code', 'displayName']}
                />
            </DialogBody>
            <DialogFooter>
                <Button text appearance="primary" onClick={prepareSelection} disabled={!selectedCount} loading={false}>
                    Apply
                </Button>
                <Button text appearance="secondary" onClick={CloseDialog}>
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default ConceptSelectionModal;

ConceptSelectionModal.propTypes = {
    partId: PropTypes.string,
    closeDialog: PropTypes.func,
    onApply: PropTypes.func,
    allItems: PropTypes.arrayOf(PropTypes.object),
    currentListItems: PropTypes.arrayOf(PropTypes.object)
};
